/* Copyright (c) Trimble Inc. All rights reserved. */

import { getTokenTable } from './Auth.db';
import { TokenDetails } from '../models/Token.model';
import { Logger } from '@tcp-base/toolkit';

class TokenTableImpl {
  /**
   * Save token details to IndexedDB
   */
  async saveTokenDetails(details: TokenDetails): Promise<void> {
    try {
      const tokenTable = await getTokenTable();

      // Store each field directly with just the value (key -> value, not key -> {key, value})
      if (details.access_token) {
        await tokenTable.put('access_token', details.access_token);
      }
      if (details.id_token) {
        await tokenTable.put('id_token', details.id_token);
      }
      if (details.expiry_time !== null && details.expiry_time !== undefined) {
        await tokenTable.put('expiry_time', details.expiry_time);
      }
    } catch (error) {
      Logger.error('Failed to save token details to IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Get token details from IndexedDB
   */
  async getTokenDetails(): Promise<TokenDetails> {
    try {
      const tokenTable = await getTokenTable();

      // Get individual values directly by key
      const tokenDetails: TokenDetails = {};

      const accessToken = await tokenTable.get('access_token');
      if (accessToken !== undefined) {
        tokenDetails.access_token = accessToken;
      }

      const idToken = await tokenTable.get('id_token');
      if (idToken !== undefined) {
        tokenDetails.id_token = idToken;
      }

      const expiryTime = await tokenTable.get('expiry_time');
      if (expiryTime !== undefined) {
        tokenDetails.expiry_time = expiryTime;
      }

      return tokenDetails;
    } catch (error) {
      Logger.error('Failed to get token details from IndexedDB Table:', error);
      throw error;
    }
  }

  /**
   * Remove all token details from IndexedDB
   */
  async removeTokenDetails(): Promise<void> {
    try {
      const tokenTable = await getTokenTable();

      // Clear all token data from the token table
      await tokenTable.clear();
    } catch (error) {
      Logger.error('Failed to remove token details from IndexedDB:', error);
      throw error;
    }
  }
}

// Create singleton instance
export const TokenTable = new TokenTableImpl();
